import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from "firebase/firestore";
import type { CanonicalCase } from "../../domain/case/canonical-case";
import {
  decodeCanonicalCase,
  encodeCanonicalCase,
  makeCaseTombstone,
} from "../../domain/case/case-adapter";
import { getFirebaseServices } from "./firebase";
import {
  recordRevision,
  submitSyncV3Mutations,
  type JsonRecord,
  type SyncV3MutationInput,
  type SyncV3MutationResult,
} from "./sync-v3-client";

const batchLimit = 25;

function assertCurrentUser(uid: string): void {
  if (!uid || getFirebaseServices().auth.currentUser?.uid !== uid) {
    throw new Error("The requested workspace does not belong to the signed-in account.");
  }
}

function caseCollection(uid: string) {
  assertCurrentUser(uid);
  return collection(getFirebaseServices().firestore, "users", uid, "cases");
}

function caseDocument(uid: string, caseId: string) {
  assertCurrentUser(uid);
  if (!caseId.trim() || caseId.includes("/")) throw new Error("Invalid case identifier.");
  return doc(getFirebaseServices().firestore, "users", uid, "cases", caseId);
}

function asRecord(value: unknown): JsonRecord {
  return value && typeof value === "object" && !Array.isArray(value) ? value as JsonRecord : {};
}

function clearedDataPaths(previous: unknown, next: unknown, prefix = "data"): string[] {
  const oldRecord = asRecord(previous);
  const nextRecord = asRecord(next);
  const cleared: string[] = [];
  for (const [key, oldValue] of Object.entries(oldRecord)) {
    if (Object.hasOwn(nextRecord, key)) {
      const newValue = nextRecord[key];
      if (oldValue && newValue && typeof oldValue === "object" && typeof newValue === "object"
        && !Array.isArray(oldValue) && !Array.isArray(newValue)) {
        cleared.push(...clearedDataPaths(oldValue, newValue, `${prefix}.${key}`));
      }
      continue;
    }
    cleared.push(`${prefix}.${key}`);
  }
  return cleared;
}

function resultCase(result: SyncV3MutationResult): CanonicalCase | null {
  return result.canonicalPayload ? decodeCanonicalCase(result.canonicalPayload, result.entityId) : null;
}

function payloadsEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (Array.isArray(left) || Array.isArray(right)) {
    return Array.isArray(left) && Array.isArray(right) && left.length === right.length
      && left.every((item, index) => payloadsEqual(item, right[index]));
  }
  if (left && right && typeof left === "object" && typeof right === "object") {
    const leftRecord = left as JsonRecord;
    const rightRecord = right as JsonRecord;
    const leftKeys = Object.keys(leftRecord).sort();
    const rightKeys = Object.keys(rightRecord).sort();
    return leftKeys.length === rightKeys.length
      && leftKeys.every((key, index) => key === rightKeys[index]
        && payloadsEqual(leftRecord[key], rightRecord[key]));
  }
  return false;
}

function appliedCreateOrRetry(result: SyncV3MutationResult, incoming: CanonicalCase): CanonicalCase {
  const current = resultCase(result);
  if (result.status === "applied" && current) return current;
  if (result.status === "rejected" && current
    && payloadsEqual(encodeCanonicalCase(current), encodeCanonicalCase(incoming))) return current;
  if (current) throw new CaseConflictError(current);
  throw new Error(result.message ?? "The case could not be saved.");
}

export class CaseConflictError extends Error {
  readonly current: CanonicalCase;

  constructor(current: CanonicalCase) {
    super("This case changed on another device. Review the latest version before saving.");
    this.name = "CaseConflictError";
    this.current = current;
  }
}

export interface CaseLoadProgress {
  loaded: number;
  complete: boolean;
}

export async function fetchAllCases(
  uid: string,
  options: {
    pageSize?: number;
    signal?: AbortSignal;
    onProgress?: (progress: CaseLoadProgress) => void;
  } = {},
): Promise<CanonicalCase[]> {
  const pageSize = Math.min(Math.max(options.pageSize ?? 1_500, 100), 5_000);
  const output: CanonicalCase[] = [];
  let cursor: QueryDocumentSnapshot | undefined;

  do {
    if (options.signal?.aborted) throw new DOMException("Case loading was cancelled.", "AbortError");
    const constraints = cursor
      ? [orderBy(documentId()), startAfter(cursor), limit(pageSize)]
      : [orderBy(documentId()), limit(pageSize)];
    const snapshot = await getDocs(query(caseCollection(uid), ...constraints));
    for (const item of snapshot.docs) output.push(decodeCanonicalCase(item.data(), item.id));
    cursor = snapshot.docs.at(-1);
    options.onProgress?.({ loaded: output.length, complete: snapshot.size < pageSize });
    if (snapshot.size < pageSize) cursor = undefined;
  } while (cursor);

  return output;
}

export async function fetchCase(uid: string, caseId: string): Promise<CanonicalCase | null> {
  const snapshot = await getDoc(caseDocument(uid, caseId));
  return snapshot.exists() ? decodeCanonicalCase(snapshot.data(), snapshot.id) : null;
}

export async function createCase(uid: string, value: CanonicalCase): Promise<CanonicalCase> {
  assertCurrentUser(uid);
  const [result] = await submitSyncV3Mutations(uid, [{
    entityType: "case",
    entityId: value.id,
    operation: "create",
    baseRevision: 0,
    payload: encodeCanonicalCase(value),
  }]);
  return appliedCreateOrRetry(result!, value);
}

export async function updateCase(
  uid: string,
  value: CanonicalCase,
  expectedUpdatedAt: string,
): Promise<CanonicalCase> {
  const snapshot = await getDoc(caseDocument(uid, value.id));
  if (!snapshot.exists()) throw new Error("The case no longer exists in the cloud workspace.");
  const current = decodeCanonicalCase(snapshot.data(), snapshot.id);
  if (current.updatedAt !== expectedUpdatedAt) throw new CaseConflictError(current);
  const payload = encodeCanonicalCase(value);
  const [result] = await submitSyncV3Mutations(uid, [{
    entityType: "case",
    entityId: value.id,
    operation: value.isDeleted ? "delete" : current.isDeleted ? "restore" : "upsert",
    baseRevision: recordRevision(snapshot.data()),
    payload,
    explicitlyClearedPaths: clearedDataPaths(asRecord(snapshot.data()).data, payload.data),
  }]);
  const persisted = resultCase(result!);
  if (result?.status === "applied" && persisted) return persisted;
  if (persisted) throw new CaseConflictError(persisted);
  throw new Error(result?.message ?? "The case could not be updated.");
}

export async function softDeleteCase(
  uid: string,
  current: CanonicalCase,
  expectedUpdatedAt: string,
): Promise<CanonicalCase> {
  return updateCase(uid, makeCaseTombstone(current), expectedUpdatedAt);
}

export interface BulkWriteProgress {
  committed: number;
  total: number;
}

export class PartialCaseWriteError extends Error {
  readonly committedCaseIds: string[];

  constructor(committedCaseIds: string[], cause: unknown) {
    super(`${committedCaseIds.length.toLocaleString()} cases were saved before the cloud write stopped. The same staged import can be retried safely.`, { cause });
    this.name = "PartialCaseWriteError";
    this.committedCaseIds = committedCaseIds;
  }
}

export class PartialCaseUpdateError extends Error {
  readonly committedCaseIds: string[];

  constructor(committedCaseIds: string[], cause: unknown) {
    super(`${committedCaseIds.length.toLocaleString()} case changes were committed before the cloud update stopped. Studio will reload the workspace before another edit.`, { cause });
    this.name = "PartialCaseUpdateError";
    this.committedCaseIds = committedCaseIds;
  }
}

export async function createCasesInBatches(
  uid: string,
  values: CanonicalCase[],
  onProgress?: (progress: BulkWriteProgress) => void,
): Promise<string[]> {
  assertCurrentUser(uid);
  const committed: string[] = [];
  for (let offset = 0; offset < values.length; offset += batchLimit) {
    const chunk = values.slice(offset, offset + batchLimit);
    try {
      const results = await submitSyncV3Mutations(uid, chunk.map((value): SyncV3MutationInput => ({
        entityType: "case",
        entityId: value.id,
        operation: "create",
        baseRevision: 0,
        payload: encodeCanonicalCase(value),
      })));
      results.forEach((result, index) => {
        const incoming = chunk[index]!;
        Object.assign(incoming, appliedCreateOrRetry(result, incoming));
      });
    } catch (reason) {
      if (reason instanceof CaseConflictError && committed.length === 0) throw reason;
      throw new PartialCaseWriteError(committed, reason);
    }
    committed.push(...chunk.map((item) => item.id));
    onProgress?.({ committed: committed.length, total: values.length });
  }
  return committed;
}

export interface ExpectedCaseWrite {
  value: CanonicalCase;
  expectedUpdatedAt: string;
}

export async function updateCasesWithConflictChecks(
  uid: string,
  writes: ExpectedCaseWrite[],
  onProgress?: (progress: BulkWriteProgress) => void,
): Promise<void> {
  let committed = 0;
  const committedCaseIds: string[] = [];
  for (let offset = 0; offset < writes.length; offset += batchLimit) {
    const chunk = writes.slice(offset, offset + batchLimit);
    try {
      const snapshots = await Promise.all(chunk.map((item) => getDoc(caseDocument(uid, item.value.id))));
      const inputs = chunk.map((item, index): SyncV3MutationInput => {
        const snapshot = snapshots[index]!;
        if (!snapshot.exists()) throw new Error(`Case ${item.value.id} no longer exists.`);
        const current = decodeCanonicalCase(snapshot.data(), snapshot.id);
        if (current.updatedAt !== item.expectedUpdatedAt) throw new CaseConflictError(current);
        const payload = encodeCanonicalCase(item.value);
        return {
          entityType: "case",
          entityId: item.value.id,
          operation: item.value.isDeleted ? "delete" : current.isDeleted ? "restore" : "upsert",
          baseRevision: recordRevision(snapshot.data()),
          payload,
          explicitlyClearedPaths: clearedDataPaths(asRecord(snapshot.data()).data, payload.data),
        };
      });
      const results = await submitSyncV3Mutations(uid, inputs);
      results.forEach((result, index) => {
        const persisted = resultCase(result);
        if (result.status === "applied" && persisted) {
          Object.assign(chunk[index]!.value, persisted);
          return;
        }
        if (persisted) throw new CaseConflictError(persisted);
        throw new Error(result.message ?? `Case ${chunk[index]!.value.id} could not be updated.`);
      });
    } catch (reason) {
      if (committedCaseIds.length) throw new PartialCaseUpdateError(committedCaseIds, reason);
      throw reason;
    }
    committed += chunk.length;
    committedCaseIds.push(...chunk.map((item) => item.value.id));
    onProgress?.({ committed, total: writes.length });
  }
}

export function observeCaseChanges(
  uid: string,
  sinceIso: string,
  observer: (values: CanonicalCase[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const changes = query(
    caseCollection(uid),
    where("updatedAt", ">", sinceIso),
    orderBy("updatedAt", "desc"),
    limit(2_000),
  );
  return onSnapshot(
    changes,
    (snapshot) => {
      const changed = snapshot.docChanges()
        .filter((change) => change.type !== "removed")
        .map((change) => decodeCanonicalCase(change.doc.data(), change.doc.id));
      if (changed.length) observer(changed);
    },
    (error) => onError?.(error),
  );
}
